import * as Api from '@ViewModels';
import * as React from 'react';
import { Field, FieldDescription, FieldLabel, FieldLegend, FieldSet } from '../../../components-v2/Field';
import { Input } from '../../../components-v2/Input';
import { TextArea } from '../../../components-v2/TextArea';

const DEFAULT_REQUEST_NAME = 'Files Requested';

const COPY = {
	upload: {
		legend: 'Request page details',
		intro: 'What your contact sees when they open the link to upload files.',
		titleLabel: 'What do you need?',
		descriptionLabel: 'Instructions (optional)',
		descriptionPlaceholder: 'What should they upload?',
	},
	download: {
		legend: 'Download page details',
		intro: 'What your contact sees when they open the link to download files.',
		titleLabel: 'Title (optional)',
		descriptionLabel: 'Message (optional)',
		descriptionPlaceholder: 'What are these files?',
	},
};

interface SharedProps {
	/** The email subject; the server names a file request after it when no name is given. */
	subject?: string;
	note?: string;
	readonly?: boolean;
}

interface LinkPageDetailsProps extends SharedProps {
	kind: keyof typeof COPY;
	title: string | undefined;
	description: string | undefined;
	onTitleChange: (title: string) => void;
	onDescriptionChange: (description: string) => void;
}

function LinkPageDetails({
	kind,
	title,
	description,
	onTitleChange,
	onDescriptionChange,
	subject,
	note,
	readonly,
}: LinkPageDetailsProps) {
	const titleId = React.useId();
	const descriptionId = React.useId();
	const copy = COPY[kind];
	const subjectName = kind === 'upload' ? subject?.trim() : undefined;
	const isTitleFromSubject = title === undefined && !!subjectName;

	return (
		<FieldSet>
			<FieldLegend>{copy.legend}</FieldLegend>
			<FieldDescription>{copy.intro}</FieldDescription>
			{note && <FieldDescription>{note}</FieldDescription>}
			<Field>
				<FieldLabel htmlFor={titleId}>{copy.titleLabel}</FieldLabel>
				<Input
					id={titleId}
					value={title ?? subjectName ?? ''}
					placeholder={kind === 'upload' ? subjectName || DEFAULT_REQUEST_NAME : undefined}
					disabled={readonly}
					onChange={e => onTitleChange(e.target.value)}
				/>
				<FieldDescription>
					{isTitleFromSubject ? 'Starts as the email subject.' : 'Your contact sees this at the top of the page.'}
				</FieldDescription>
			</Field>
			<Field>
				<FieldLabel htmlFor={descriptionId}>{copy.descriptionLabel}</FieldLabel>
				<TextArea
					id={descriptionId}
					value={description ?? ''}
					placeholder={copy.descriptionPlaceholder}
					rows={3}
					disabled={readonly}
					onChange={e => onDescriptionChange(e.target.value)}
				/>
			</Field>
		</FieldSet>
	);
}

export interface FileRequestDetailsPanelProps extends SharedProps {
	value: Api.IFileRequestCreationOptions | null | undefined;
	onChange: (value: Api.IFileRequestCreationOptions) => void;
}

export function FileRequestDetailsPanel({ value, onChange, ...shared }: FileRequestDetailsPanelProps) {
	return (
		<LinkPageDetails
			{...shared}
			kind='upload'
			title={value?.name}
			description={value?.description}
			onTitleChange={name => onChange({ ...value, name })}
			onDescriptionChange={description => onChange({ ...value, description })}
		/>
	);
}

export interface FileSendDetailsPanelProps extends Omit<SharedProps, 'subject'> {
	value: Api.ISharedLinkCreationOptions | null | undefined;
	onChange: (value: Api.ISharedLinkCreationOptions) => void;
}

export function FileSendDetailsPanel({ value, onChange, ...shared }: FileSendDetailsPanelProps) {
	return (
		<LinkPageDetails
			{...shared}
			kind='download'
			title={value?.title}
			description={value?.description}
			onTitleChange={title => onChange({ ...value, title })}
			onDescriptionChange={description => onChange({ ...value, description })}
		/>
	);
}
